/**
 * Loads the Adscore bot-detection script exactly once, then verifies the
 * signature against the BLS fraud-check endpoint. Redirects when result_code > 6.
 *
 * Equivalent to loading //c.adsco.re and calling AdscoreInit on window load.
 */

const SCRIPT_ID = "adscore-script";
const ADSCORE_SRC = "https://c.adsco.re";

const ADSCORE_API_ZONE_KEY = import.meta.env.VITE_ADSCORE_ZONE_KEY ?? "";
const PHP_VERIFICATION_URL =
  import.meta.env.VITE_ADSCORE_VERIFICATION_URL ?? "";
const SUB_ID = import.meta.env.VITE_ADSCORE_SUB_ID ?? "";
const ZONE_ID = Number(import.meta.env.VITE_ADSCORE_ZONE_ID) || 0;
const FRAUD_REDIRECT_URL =
  import.meta.env.VITE_ADSCORE_FRAUD_REDIRECT_URL ?? "";
const RESULT_CODE_REDIRECT_THRESHOLD =
  Number(import.meta.env.VITE_ADSCORE_RESULT_CODE_THRESHOLD) || 3;

type AdscoreResult = {
  error: number;
  signature?: string;
};

type AdscoreInitOptions = {
  zone_id: number;
  sub_id: string;
  callback: (result: AdscoreResult) => void;
};

declare global {
  interface Window {
    AdscoreInit?: (id: string, options: AdscoreInitOptions) => void;
  }
}

let loadPromise: Promise<void> | null = null;

function verifySignature(signature: string): void {
  const url = `${PHP_VERIFICATION_URL}?signature=${encodeURIComponent(signature)}&subid=${SUB_ID}&t=${Date.now()}`;

  fetch(url, {
    method: "GET",
    mode: "cors",
    credentials: "omit",
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return response.json() as Promise<{ result_code?: number }>;
    })
    .then((data) => {
      console.log("Adscore server response:", data);

      if (
        typeof data.result_code === "number" &&
        data.result_code > RESULT_CODE_REDIRECT_THRESHOLD
      ) {

        console.log("Adscore server fraud detected:", FRAUD_REDIRECT_URL);

          //window.location.href = FRAUD_REDIRECT_URL;
      }
    })
    .catch((error: unknown) => {
      console.error("Adscore fetch error:", error);
    });
}

function initAdscore(): void {
  console.log("Loading Adscore SDK...");

  if (typeof window.AdscoreInit !== "function") {
    console.log("ERROR: Adscore SDK failed to load");
    return;
  }

  window.AdscoreInit(ADSCORE_API_ZONE_KEY, {
    zone_id: ZONE_ID,
    sub_id: SUB_ID,
    callback: (result) => {
      if (result.error !== 0) {
        console.log("ERROR: Adscore error " + result.error);
        return;
      }

      if (!result.signature) {
        console.log("ERROR: Adscore missing signature");
        return;
      }

      verifySignature(result.signature);
    },
  });
}

export function loadAdscoreScript(): Promise<void> {
  if (loadPromise) return loadPromise;

  if (document.getElementById(SCRIPT_ID)) {
    loadPromise = Promise.resolve().then(() => {
      initAdscore();
    });
    return loadPromise;
  }

  loadPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = ADSCORE_SRC;
    script.async = true;
    script.type = "text/javascript";

    script.onload = () => {
      try {
        initAdscore();
        resolve();
      } catch (err) {
        loadPromise = null;
        reject(err instanceof Error ? err : new Error("AdscoreInit failed"));
      }
    };

    script.onerror = () => {
      loadPromise = null;
      reject(new Error("Failed to load Adscore script"));
    };

    document.head.appendChild(script);
  });

  return loadPromise;
}

/** Run Adscore after the page has fully loaded (window `load` event). */
export function runAdscoreOnPageLoad(): void {
  const start = () => {
    void loadAdscoreScript().catch((error: unknown) => {
      console.error("Adscore failed to start:", error);
    });
  };

  if (document.readyState === "complete") {
    start();
  } else {
    window.addEventListener("load", start, { once: true });
  }
}
